import React from 'react';

import { CaseReport } from '../../models/CaseTypes';
import { formatCaseDateDisplay, formatCaseDescription, formatSurgicalTeamDisplay } from '../../utils/caseUtils';

interface InfoFieldProps {
  label: string;
  value: string;
}

function InfoField({ label, value }: InfoFieldProps): React.JSX.Element {
  const dataTestId = `case-info-card-${label.toLowerCase().replaceAll(' ', '')}`;
  return (
    <div className="flex flex-col gap-1">
      <label style={{ color: 'var(--medium-emphasis-font)', fontSize: 'var(--text-caption)' }}>
        {label}
      </label>
      <p data-testid={dataTestId}>{value}</p>
    </div>
  );
}

interface CaseInfoCardProps {
  caseReport: CaseReport;
}

const formatBooleanDisplay = (value: boolean | undefined | null): string => {
  return value ? 'Yes' : 'No';
};

const formatStageDisplay = (caseReport: CaseReport): string => {
  return caseReport.isStaged ?
    caseReport.stage ? `Part ${caseReport.stage}` : 'Yes' :
    'No';
};

export function CaseInfoCard({ caseReport }: CaseInfoCardProps): React.JSX.Element {
  return (
    <div
      className="bg-card border border-border"
      style={{
        borderRadius: 'var(--radius-card)',
        padding: 'var(--spacing-3)',
      }}
    >
      {/* Below 440px: 3 rows - Procedure Type | Rev+Staged+Date | Surgical Team */}
      <div
        className="flex min-[440px]:hidden flex-col"
        style={{ gap: 'var(--spacing-2)' }}
      >
        <div className="w-full">
          <InfoField label="Procedure Type" value={formatCaseDescription(caseReport, true)}/>
        </div>
        <div
          className="flex w-full"
          style={{ gap: 'var(--spacing-4)' }}
        >
          <InfoField label="Revision" value={formatBooleanDisplay(caseReport.isRevision)} />
          <InfoField label="Staged" value={formatStageDisplay(caseReport)} />
          <InfoField label="Case Date" value={formatCaseDateDisplay(caseReport.date, true)} />
        </div>
        <div className="w-full">
          <InfoField label="Surgical Team" value={formatSurgicalTeamDisplay(caseReport.actors)}/>
        </div>
      </div>

      {/* 440px to 743px: 2 rows - Procedure Type | Rev+Staged+Date+Surgical Team */}
      <div
        className="hidden min-[440px]:flex min-[744px]:hidden flex-col"
        style={{ gap: 'var(--spacing-2)' }}
      >
        <div className="w-full">
          <InfoField label="Procedure Type" value={formatCaseDescription(caseReport, true)}/>
        </div>
        <div
          className="flex w-full"
          style={{ gap: 'var(--spacing-4)' }}
        >
          <InfoField label="Revision" value={formatBooleanDisplay(caseReport.isRevision)} />
          <InfoField label="Staged" value={formatStageDisplay(caseReport)} />
          <InfoField label="Case Date" value={formatCaseDateDisplay(caseReport.date, true)} />
          <div className="max-w-[250px]">
            <InfoField label="Surgical Team" value={formatSurgicalTeamDisplay(caseReport.actors)}/>
          </div>
          
        </div>
      </div>

      {/* 744px and above: 1 row - All fields in single row */}
      <div
        className="hidden min-[744px]:flex"
        style={{ gap: 'var(--spacing-4)' }}
      >
        <InfoField label="Procedure Type" value={formatCaseDescription(caseReport, true)}/>
        <InfoField label="Revision" value={formatBooleanDisplay(caseReport.isRevision)} />
        <InfoField label="Staged" value={formatStageDisplay(caseReport)} />
        <InfoField label="Case Date" value={formatCaseDateDisplay(caseReport.date, true)} />
        <InfoField label="Surgical Team" value={formatSurgicalTeamDisplay(caseReport.actors)}/>
      </div>
    </div>
  );
}
